/**
 * 可疑情况上报页面 - 防拐公益
 */

import { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import type { SuspectedCaseType } from '../types';

const CASE_TYPES: { type: SuspectedCaseType; label: string; icon: string; desc: string }[] = [
  {
    type: 'suspected_trafficking',
    label: '疑似拐卖',
    icon: '🚨',
    desc: '发现疑似被拐卖的可疑人员或车辆'
  },
  {
    type: 'begging',
    label: '流浪乞讨',
    icon: '🧒',
    desc: '发现疑似被拐带或强迫乞讨的儿童'
  },
  {
    type: 'suspicious',
    label: '可疑人员',
    icon: '👤',
    desc: '发现行迹可疑、胁迫儿童的人员'
  },
  {
    type: 'other',
    label: '其他情况',
    icon: '📝',
    desc: '其他需要上报的情况'
  }
];

export default function ReportPage() {
  const { saveSuspectedReport } = useApp();
  const [step, setStep] = useState<'type' | 'photo' | 'desc' | 'done'>('type');
  const [caseType, setCaseType] = useState<SuspectedCaseType | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // 选择情况类型
  const handleSelectType = (type: SuspectedCaseType) => {
    setCaseType(type);
    setStep('photo');
  };

  // 拍照
  const handleTakePhoto = async () => {
    if (photos.length >= 3) {
      alert('最多上传3张照片');
      return;
    }

    setIsCapturing(true);
    try {
      // 创建临时video元素
      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      video.srcObject = stream;
      await video.play();

      // 等待用户确认
      const confirmed = await new Promise<boolean>(resolve => {
        const overlay = document.createElement('div');
        overlay.style.cssText = `
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(0,0,0,0.9);
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
        `;

        const img = document.createElement('img');
        img.style.cssText = 'width: 80%; max-width: 300px; border-radius: 12px;';
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        img.src = canvas.toDataURL('image/jpeg', 0.7);

        const btnGroup = document.createElement('div');
        btnGroup.style.cssText = 'display: flex; gap: 16px; margin-top: 20px;';

        const confirmBtn = document.createElement('button');
        confirmBtn.textContent = '✓ 确认';
        confirmBtn.style.cssText = 'padding: 12px 32px; background: #2a9d8f; color: white; border: none; border-radius: 8px; font-size: 16px;';
        confirmBtn.onclick = () => {
          stream.getTracks().forEach(t => t.stop());
          document.body.removeChild(overlay);
          resolve(true);
        };

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = '✗ 重拍';
        cancelBtn.style.cssText = 'padding: 12px 32px; background: #666; color: white; border: none; border-radius: 8px; font-size: 16px;';
        cancelBtn.onclick = () => {
          stream.getTracks().forEach(t => t.stop());
          document.body.removeChild(overlay);
          resolve(false);
        };

        btnGroup.appendChild(confirmBtn);
        btnGroup.appendChild(cancelBtn);
        overlay.appendChild(img);
        overlay.appendChild(btnGroup);
        document.body.appendChild(overlay);
      });

      if (confirmed) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        const photoData = canvas.toDataURL('image/jpeg', 0.6);
        setPhotos([...photos, photoData]);
      }
    } catch (error) {
      console.error('[上报] 拍照失败:', error);
      alert('拍照失败，请重试');
    } finally {
      setIsCapturing(false);
    }
  };

  // 提交
  const handleSubmit = async () => {
    if (!caseType) return;
    if (photos.length === 0) {
      alert('请至少拍摄一张照片');
      return;
    }
    if (!consent) {
      alert('请勾选同意声明');
      return;
    }

    setIsSubmitting(true);
    try {
      await saveSuspectedReport({
        caseType,
        photos,
        description,
        witnessConsent: consent
      });
      setStep('done');
    } catch (error) {
      console.error('[上报] 提交失败:', error);
      alert('提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 重新开始
  const handleReset = () => {
    setStep('type');
    setCaseType(null);
    setPhotos([]);
    setDescription('');
    setConsent(false);
  };

  return (
    <div className="min-h-screen bg-bg-dark flex flex-col">
      {/* 头部 */}
      <header className="p-4 border-b border-white/10">
        <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
          <span>📝</span>
          <span>可疑情况上报</span>
        </h1>
        <p className="text-sm text-text-muted mt-1">
          发现疑似拐卖行为？快速记录，保护隐私
        </p>
      </header>

      {/* 进度 */}
      <div className="flex items-center justify-center p-4 gap-4">
        {['type', 'photo', 'desc', 'done'].map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
              ${step === s ? 'bg-sos-red text-white' :
                ['type', 'photo', 'desc', 'done'].indexOf(step) > i ? 'bg-success text-white' : 'bg-white/10 text-text-muted'}
            `}>
              {i + 1}
            </div>
            {i < 3 && <div className="w-8 h-0.5 bg-white/10 mx-1" />}
          </div>
        ))}
      </div>

      {/* 内容 */}
      <main className="flex-1 p-4">
        {/* 步骤1: 选择类型 */}
        {step === 'type' && (
          <div className="space-y-3">
            <p className="text-text-muted text-sm mb-4">
              请选择您发现的可疑情况类型：
            </p>
            {CASE_TYPES.map(item => (
              <button
                key={item.type}
                onClick={() => handleSelectType(item.type)}
                className="w-full bg-bg-light rounded-xl p-4 flex items-start gap-4 hover:bg-white/5 transition-colors text-left"
              >
                <span className="text-3xl">{item.icon}</span>
                <div>
                  <h3 className="font-bold text-text-primary">{item.label}</h3>
                  <p className="text-sm text-text-muted mt-1">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* 步骤2: 拍照 */}
        {step === 'photo' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-text-muted text-sm">
                请拍摄现场照片（{photos.length}/3张）
              </p>
              <button
                onClick={() => setStep('desc')}
                className="text-sos-red text-sm"
                disabled={photos.length === 0}
              >
                {photos.length > 0 ? '下一步 →' : '跳过'}
              </button>
            </div>

            {/* 照片预览 */}
            {photos.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {photos.map((photo, i) => (
                  <div key={i} className="relative flex-shrink-0">
                    <img
                      src={photo}
                      alt={`照片${i + 1}`}
                      className="w-24 h-24 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-sos-red rounded-full text-white text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 拍照按钮 */}
            <button
              onClick={handleTakePhoto}
              disabled={isCapturing || photos.length >= 3}
              className="w-full py-8 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-2 hover:border-sos-red/50 transition-colors disabled:opacity-50"
            >
              <span className="text-4xl">{isCapturing ? '⏳' : '📷'}</span>
              <span className="text-text-muted">
                {isCapturing ? '拍摄中...' : '点击拍摄照片'}
              </span>
            </button>

            {/* 提示 */}
            <div className="bg-warning/10 rounded-lg p-3">
              <p className="text-warning text-sm">
                💡 提示：拍摄时尽量包含人物特征、环境照片，不要强行拍摄，保护自身安全
              </p>
            </div>
          </div>
        )}

        {/* 步骤3: 描述 */}
        {step === 'desc' && (
          <div className="space-y-4">
            <div>
              <label className="text-text-muted text-sm mb-2 block">
                情况描述（选填）
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="简要描述您发现的可疑情况，如：人员外貌特征、车辆信息、时间地点等..."
                className="w-full h-32 bg-bg-light rounded-xl p-4 text-text-primary resize-none"
                maxLength={500}
              />
              <p className="text-text-muted/60 text-xs mt-1 text-right">
                {description.length}/500
              </p>
            </div>

            {/* 同意声明 */}
            <div className="bg-bg-light rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-sos-red"
                />
                <div className="text-sm text-text-muted">
                  <p>
                    本人确认所提供的信息真实有效，愿意配合相关部门调查。
                    上报为匿名行为，个人信息不会被公开。
                  </p>
                  <p className="text-sos-red mt-2">
                    ⚠️ 故意提供虚假信息将承担法律责任
                  </p>
                </div>
              </label>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!consent || isSubmitting}
              className={`w-full py-4 rounded-xl font-bold text-lg ${
                consent && !isSubmitting
                  ? 'bg-sos-red text-white'
                  : 'bg-white/10 text-text-muted'
              }`}
            >
              {isSubmitting ? '提交中...' : '匿名提交'}
            </button>
          </div>
        )}

        {/* 完成 */}
        {step === 'done' && (
          <div className="text-center py-12">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              感谢您的正义之举！
            </h2>
            <p className="text-text-muted mb-6">
              您的上报已加密提交<br />
              志愿者将尽快核实处理
            </p>
            <div className="bg-bg-light rounded-xl p-4 mb-6 text-left">
              <p className="text-text-muted text-sm">
                📋 <strong>温馨提示：</strong><br /><br />
                • 您的上报信息采用匿名方式<br />
                • 照片和位置信息已加密<br />
                • 如需紧急帮助请拨打110<br />
                • 志愿者核实后会自动联动警方
              </p>
            </div>
            <button
              onClick={handleReset}
              className="px-8 py-3 bg-sos-red text-white rounded-lg font-medium"
            >
              继续上报
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
